import React from "react";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Collapse from "@mui/material/Collapse";
import { useMediaQuery } from "@mui/material";
import Close from "@mui/icons-material/Close";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useCMSData } from "app/hooks/useCMSData";
import MenuIcon from "app/assets/vectors/jsx/MenuIcon";
import { useUrlFilters } from "app/hooks/useUrlFilters";
import { NavLink, useLocation } from "react-router-dom";
import { colors, customBreakpointsMax } from "app/theme";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { SkipToMainButton } from "app/components/skip-to-main-btn";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { HeaderButtons } from "app/components/header/sub-components/buttons";
import { HeaderExpandedView } from "app/components/header/sub-components/expanded-view";
import { getHeaderButtonItems } from "app/components/header/sub-components/buttons/data";
import { ReactComponent as HeaderToolbarLogo } from "app/assets/vectors/HeaderToolbarLogo.svg";

const containerSx = {
  [customBreakpointsMax.tablet]: {
    padding: "0 30px",
  },
  [customBreakpointsMax.mobile]: {
    padding: "0 24px",
  },
};

export const Header: React.FC = () => {
  useUrlFilters();
  const cmsData = useCMSData({ returnData: true });
  const { hash, pathname, search } = useLocation();
  const mobile = useMediaQuery("(max-width: 743px)");

  const activeButton = useStoreState(
    (state) => state.activeHeaderButton.activeButton
  );
  const setActiveButton = useStoreActions(
    (state) => state.activeHeaderButton.setActiveButton
  );
  const clearLoginFlow = useStoreActions(
    (actions) => actions.activeHeaderButton.clearLoginFlow
  );
  const showBanner = useStoreState((state) => state.showHeaderBanner.value);
  const setShowBanner = useStoreActions(
    (actions) => actions.showHeaderBanner.setValue
  );

  const [mobileMenu, setMobileMenu] = React.useState<boolean>(false);

  const HEADER_BUTTON_ITEMS = React.useMemo(
    () => getHeaderButtonItems(cmsData),
    [cmsData]
  );

  const handleButtonClick = (key: string) => {
    if (key !== HEADER_BUTTON_ITEMS[4].key) {
      clearLoginFlow();
    }
    setActiveButton(key === activeButton ? "" : key);
  };

  const handleBannerClose = () => {
    setShowBanner(false);
  };

  React.useEffect(() => {
    setActiveButton("");
    setMobileMenu(false);
    setTimeout(() => {
      if (
        hash.length === 0 &&
        search.indexOf("&e=") === -1 &&
        search.indexOf("?e=") === -1
      ) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 200);
  }, [pathname]);

  return (
    <Box sx={{ zIndex: 1302, flexGrow: 1, top: 0, position: "sticky" }}>
      <AppBar position="static">
        <Container maxWidth="lg" disableGutters sx={containerSx}>
          <ClickAwayListener onClickAway={() => handleButtonClick("")}>
            <Toolbar
              sx={{
                display: "block",
                position: "unset",
                background: colors.secondary.lightGrey,
              }}
              data-cy="navbar"
            >
              <Box
                sx={{
                  width: "100%",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <SkipToMainButton />
                <NavLink
                  to="/"
                  style={{ display: "flex", alignItems: "center" }}
                  aria-label="AIDA homepage"
                >
                  <HeaderToolbarLogo />
                </NavLink>
                {mobile ? (
                  <IconButton
                    onClick={() => setMobileMenu(!mobileMenu)}
                    aria-label="Toggle menu"
                  >
                    <MenuIcon />
                  </IconButton>
                ) : (
                  <HeaderButtons
                    buttons={getHeaderButtonItems(cmsData)}
                    activeButton={activeButton}
                    handleButtonClick={handleButtonClick}
                  />
                )}
              </Box>
              {activeButton && !mobile && (
                <HeaderExpandedView activeButton={activeButton} />
              )}
              {mobile && (
                <Collapse
                  sx={{
                    width: "100vw",
                    position: "absolute",
                    left: 0,
                  }}
                  in={mobileMenu || !!activeButton}
                >
                  <HeaderButtons
                    buttons={getHeaderButtonItems(cmsData)}
                    activeButton={activeButton}
                    handleButtonClick={handleButtonClick}
                  />
                </Collapse>
              )}
            </Toolbar>
          </ClickAwayListener>
        </Container>
        {showBanner && (
          <Box
            sx={{
              top: "51px",
              width: "100%",
              height: "44px",
              backgroundColor: "#2e4063",
            }}
          >
            <Container
              maxWidth="lg"
              disableGutters
              sx={{ ...containerSx, height: "100%" }}
            >
              <Box
                sx={{
                  gap: "10px",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    gap: "10px",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InfoOutlined htmlColor={colors.primary.white} />
                  <Typography
                    variant="h5"
                    fontSize="16px"
                    component="div"
                    fontWeight="700"
                    color={colors.primary.white}
                    sx={{
                      a: {
                        color: colors.primary.white,
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Welcome to new version of AIDA. You can still access the
                    IATI Publication Tools at{" "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://archive.aida.tools"
                    >
                      AIDA Version 2.0
                    </a>
                    .
                  </Typography>
                </Box>
                <IconButton onClick={handleBannerClose}>
                  <Close htmlColor={colors.primary.white} />
                </IconButton>
              </Box>
            </Container>
          </Box>
        )}
      </AppBar>
    </Box>
  );
};
